import { Injectable, NgZone, OnDestroy, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

/*
 * Keeps text readable whatever colours a venue has chosen.
 *
 * WHY THIS EXISTS
 *
 * Each venue picks its own page background and text colour, and those are applied
 * by a stylesheet the API generates per venue. Many parts of the app have a fixed
 * background of their own - a white card, a white input, a pale box - and take
 * their text colour from the venue. A venue with white text on a black page then
 * gets white text on those white boxes, which cannot be read at all.
 *
 * Fixing that box by box never ends: there are dozens of them, they combine
 * differently for every venue, and any new screen can bring the problem back. So
 * this checks what is actually on screen instead.
 *
 * WHAT IT DOES
 *
 * After the page changes, it looks at every element showing text and works out
 * the colour behind it, by walking up the page until it reaches a solid
 * background. If the text is practically invisible against that (contrast below
 * MIN_CONTRAST - white on white is 1:1), it sets the text to near-black on a
 * light background or white on a dark one.
 *
 * It only ever acts on text that cannot be read. Light grey hint text and
 * anything else a venue has deliberately styled is left alone. Text it has
 * changed is checked again every time, with its own change removed first, so if
 * the background changes back the original colour returns.
 *
 * WHAT IT CANNOT DO
 *
 * Text over a background image or gradient is skipped: there is no single
 * colour to compare against. So is anything inside an iframe, such as the Stripe
 * card form, which manages its own colours. It does not recolour images, so a
 * dark logo on a dark page stays as it is.
 *
 * To exclude an element and everything inside it, give it data-contrast-ignore.
 */

/** Below this, text is treated as unreadable. WCAG asks 4.5 for body text; this is
 *  set far lower on purpose, to catch only text that is effectively invisible. */
export const MIN_CONTRAST = 2;

const DARK_TEXT = '#111827';
const LIGHT_TEXT = '#ffffff';

/** Marks an element whose colour this service has set. */
const FIXED_ATTRIBUTE = 'data-contrast-fixed';
const IGNORE_ATTRIBUTE = 'data-contrast-ignore';

/** Form controls show text in their value rather than in a text node. */
const FORM_CONTROLS = new Set(['INPUT', 'TEXTAREA', 'SELECT']);

/** Never contain text a person reads, or manage their own colours. */
const SKIPPED_TAGS = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEMPLATE', 'IFRAME', 'SVG', 'CANVAS', 'VIDEO', 'IMG', 'HEAD']);

const INPUT_TYPES_WITHOUT_TEXT = new Set(['hidden', 'checkbox', 'radio', 'range', 'color', 'file', 'image']);

export interface Rgba { r: number; g: number; b: number; a: number; }

/** Parses the rgb()/rgba() strings getComputedStyle returns. Null for anything else. */
export function parseColor(value: string | null | undefined): Rgba | null {
  if (!value) {
    return null;
  }

  const match = value.trim().match(/^rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)(?:\s*[,/]\s*([\d.]+%?))?\s*\)$/i);

  if (!match) {
    return value.trim().toLowerCase() === 'transparent' ? { r: 0, g: 0, b: 0, a: 0 } : null;
  }

  let alpha = 1;
  if (match[4] !== undefined) {
    alpha = match[4].endsWith('%') ? parseFloat(match[4]) / 100 : parseFloat(match[4]);
  }

  return { r: +match[1], g: +match[2], b: +match[3], a: alpha };
}

/** WCAG relative luminance, 0 (black) to 1 (white). */
export function relativeLuminance(color: Rgba): number {
  const channel = (value: number) => {
    const c = value / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };

  return 0.2126 * channel(color.r) + 0.7152 * channel(color.g) + 0.0722 * channel(color.b);
}

/** WCAG contrast ratio, 1 (identical) to 21 (black on white). */
export function contrastRatio(a: Rgba, b: Rgba): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);

  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** Lays a partly transparent colour over a solid one. */
function blend(top: Rgba, bottom: Rgba): Rgba {
  const a = top.a;
  return {
    r: top.r * a + bottom.r * (1 - a),
    g: top.g * a + bottom.g * (1 - a),
    b: top.b * a + bottom.b * (1 - a),
    a: 1,
  };
}

const WHITE: Rgba = { r: 255, g: 255, b: 255, a: 1 };

/**
 * The solid colour behind an element, or null when it cannot be known because a
 * background image or gradient is in the way.
 *
 * Walks up from the element, collecting translucent layers until it reaches an
 * opaque one. With nothing opaque all the way up, the browser's own white canvas
 * is what shows through.
 */
export function effectiveBackground(element: Element, cache?: Map<Element, Rgba | null>): Rgba | null {
  const layers: Rgba[] = [];
  let node: Element | null = element;
  let result: Rgba | null = null;
  const visited: Element[] = [];

  while (node) {
    if (cache && cache.has(node)) {
      const known = cache.get(node)!;
      result = known === null ? null : layers.reduceRight((under, layer) => blend(layer, under), known);
      break;
    }

    visited.push(node);
    const style = getComputedStyle(node);

    if (style.backgroundImage && style.backgroundImage !== 'none') {
      result = null;
      layers.length = 0;
      break;
    }

    const colour = parseColor(style.backgroundColor);

    if (colour && colour.a >= 0.99) {
      result = layers.reduceRight((under, layer) => blend(layer, under), { ...colour, a: 1 });
      break;
    }

    if (colour && colour.a > 0) {
      layers.push(colour);
    }

    node = node.parentElement;
  }

  if (!node) {
    // Reached the top with nothing opaque: the canvas behind the page is white.
    result = layers.reduceRight((under, layer) => blend(layer, under), WHITE);
  }

  // Only the element asked about is cached: a translucent ancestor's own answer
  // would need its own layers, which were not tracked separately.
  if (cache && visited.length > 0) {
    cache.set(visited[0], result);
  }

  return result;
}

/** Whether the element itself shows text: a non-blank text node directly inside it,
 *  or a form control that holds text. */
function showsText(element: Element): boolean {
  if (FORM_CONTROLS.has(element.tagName)) {
    const type = (element.getAttribute('type') || 'text').toLowerCase();
    return element.tagName !== 'INPUT' || !INPUT_TYPES_WITHOUT_TEXT.has(type);
  }

  for (let child = element.firstChild; child; child = child.nextSibling) {
    if (child.nodeType === Node.TEXT_NODE && (child.textContent || '').trim() !== '') {
      return true;
    }
  }

  return false;
}

/**
 * Checks every element under root and fixes text that cannot be read. Returns the
 * number of elements whose colour it set. Synchronous and self-contained, so it
 * can be tested directly.
 */
export function enforceReadableText(root: Element, minContrast = MIN_CONTRAST): number {
  // Remove this service's own changes first, so each element is judged on the
  // colours the page gives it now - a background that has changed back gets
  // its original text colour back.
  // The attribute holds the element's own inline colour from before, if it had
  // one ("value|important"), so that is what comes back rather than nothing.
  const restore = (element: Element) => {
    const saved = element.getAttribute(FIXED_ATTRIBUTE) || '';
    const [value, priority] = saved.split('|');
    const style = (element as HTMLElement).style;

    if (value) {
      style.setProperty('color', value, priority || '');
    } else {
      style.removeProperty('color');
    }

    element.removeAttribute(FIXED_ATTRIBUTE);
  };

  if (root.hasAttribute(FIXED_ATTRIBUTE)) {
    restore(root);
  }
  root.querySelectorAll('[' + FIXED_ATTRIBUTE + ']').forEach(restore);

  const cache = new Map<Element, Rgba | null>();
  let fixed = 0;

  const candidates: Element[] = [root, ...Array.from(root.querySelectorAll('*'))];

  for (const element of candidates) {
    if (SKIPPED_TAGS.has(element.tagName.toUpperCase()) || element.closest('[' + IGNORE_ATTRIBUTE + ']') || element.closest('svg')) {
      continue;
    }

    if (!showsText(element)) {
      continue;
    }

    // Not on screen: nothing to read, and its colours are not meaningful.
    if (element.getClientRects().length === 0) {
      continue;
    }

    const style = getComputedStyle(element);

    if (style.visibility === 'hidden' || parseFloat(style.opacity) === 0) {
      continue;
    }

    const text = parseColor(style.color);
    const background = effectiveBackground(element, cache);

    if (!text || !background) {
      continue;
    }

    const visibleText = text.a < 0.99 ? blend(text, background) : text;

    if (contrastRatio(visibleText, background) >= minContrast) {
      continue;
    }

    const readable = relativeLuminance(background) > 0.18 ? DARK_TEXT : LIGHT_TEXT;
    const inline = (element as HTMLElement).style;
    const original = inline.getPropertyValue('color');

    element.setAttribute(FIXED_ATTRIBUTE, original ? original + '|' + inline.getPropertyPriority('color') : '');
    inline.setProperty('color', readable, 'important');
    fixed++;
  }

  return fixed;
}

/**
 * Runs enforceReadableText over the page whenever it changes. Started once, by
 * AppComponent, so it covers every screen.
 */
@Injectable({ providedIn: 'root' })
export class TextContrastService implements OnDestroy {
  private readonly zone = inject(NgZone);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  private observer: MutationObserver | null = null;
  private pending: ReturnType<typeof setTimeout> | null = null;
  private readonly onStylesheetLoad = (event: Event) => {
    // The venue's stylesheet arrives after the page is first drawn, and changes
    // every colour on it.
    if ((event.target as Element)?.tagName === 'LINK') {
      this.schedule();
    }
  };

  /** Short enough to feel immediate, long enough to let a burst of changes settle. */
  private static readonly DELAY_MS = 120;

  start(): void {
    if (!this.isBrowser || this.observer || typeof MutationObserver === 'undefined') {
      return;
    }

    // Outside Angular, so neither the observer nor the colour changes it makes
    // set off change detection across the whole app.
    this.zone.runOutsideAngular(() => {
      this.observer = new MutationObserver(() => this.schedule());
      this.observer.observe(document.body, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['class', 'style', 'hidden'],
      });

      document.addEventListener('load', this.onStylesheetLoad, true);
      this.schedule();
    });
  }

  private schedule(): void {
    if (this.pending !== null) {
      clearTimeout(this.pending);
    }

    this.pending = setTimeout(() => {
      this.pending = null;
      this.run();
    }, TextContrastService.DELAY_MS);
  }

  private run(): void {
    try {
      enforceReadableText(document.body);
    } catch {
      // A failure here must never break the page it is trying to help.
    }

    // Its own colour changes are style mutations; drop them rather than
    // schedule another pass over a page that has just been checked.
    this.observer?.takeRecords();
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    this.observer = null;
    document.removeEventListener('load', this.onStylesheetLoad, true);

    if (this.pending !== null) {
      clearTimeout(this.pending);
    }
  }
}
